import { BillHeaderOcr } from "../apis/ocrBillHeaderService";
import { ReconBillUploadRow } from "../components/patient/BillAuditStage/ReconBillUploadTable";
import { isBillNoMatch } from "./billNoMatchUtils";
import { API_URL } from "./constants";
import { isSyntheticReconRow } from "./reconBillUploadUtils";

export type OcrRowCacheEntry = {
  loading: boolean;
  billHeaders: BillHeaderOcr[];
  error?: string;
};

/** OCR cache keyed by reconId so each uploaded row is independent. */
export type OcrByReconId = Record<string, OcrRowCacheEntry>;

/** @deprecated use OcrByReconId */
export type OcrByDocumentId = OcrByReconId;

/** Build absolute URL for OCR when documentUrl is relative. */
export const resolveBillUrlForOcr = (documentUrl?: string | null): string => {
  if (!documentUrl) {
    return "";
  }
  const trimmed = String(documentUrl).trim();
  if (!trimmed) {
    return "";
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  const base = (API_URL || "").replace(/\/$/, "");
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${base}${path}`;
};

const findMatchingOcrHeader = (
  billNo: string | undefined,
  billHeaders: BillHeaderOcr[],
): BillHeaderOcr | undefined => {
  if (!billNo?.trim() || !billHeaders?.length) {
    return undefined;
  }
  return billHeaders.find((header) => isBillNoMatch(billNo, header?.billNo));
};

const applyHeaderToRow = (
  row: ReconBillUploadRow,
  header: BillHeaderOcr,
  billNoMatched: boolean,
): ReconBillUploadRow => ({
  ...row,
  ocrLoading: false,
  aiPatientName: header.patientName || undefined,
  aiUhid: header.uhid || undefined,
  aiDoa: header.doa || undefined,
  aiDod: header.dod || undefined,
  aiBillNo: header.billNo || undefined,
  billNoMatched,
});

/**
 * Overlay OCR onto each recon row from that row's own cache entry only.
 */
export const applyOcrHeadersToReconRows = (
  rows: ReconBillUploadRow[],
  ocrByReconId: OcrByReconId,
): ReconBillUploadRow[] => {
  if (!rows?.length || !ocrByReconId) {
    return rows;
  }

  return rows.map((row) => {
    if (row.cancelled || isSyntheticReconRow(row)) {
      return row;
    }
    const cache = ocrByReconId[String(row.reconId)];
    if (!cache) {
      return row;
    }
    if (cache.loading) {
      return { ...row, ocrLoading: true };
    }
    if (cache.error || !cache.billHeaders?.length) {
      return { ...row, ocrLoading: false };
    }
    const matched = findMatchingOcrHeader(row.billNo, cache.billHeaders);
    if (matched) {
      return applyHeaderToRow(row, matched, true);
    }
    return applyHeaderToRow(row, cache.billHeaders[0], false);
  });
};
